import React, { useState, useEffect } from 'react';
import { slidersAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineX,
  HiOutlinePhotograph,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiMenuAlt4,
} from 'react-icons/hi';

const AdminSliders = () => {
  const [sliders, setSliders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSlider, setEditingSlider] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    buttonText: '',
    buttonLink: '',
    image: '',
    textPosition: 'left',
    order: 0,
    isActive: true,
  });

  useEffect(() => {
    fetchSliders();
  }, []);

  const fetchSliders = async () => {
    setLoading(true);
    try {
      const response = await slidersAPI.getAll();
      setSliders(response.data.sliders || response.data);
    } catch (error) {
      toast.error('Failed to fetch sliders');
    } finally {
      setLoading(false);
    }
  };

  const openModal = (slider = null) => {
    if (slider) {
      setEditingSlider(slider);
      setFormData({
        title: slider.title || '',
        subtitle: slider.subtitle || '',
        description: slider.description || '',
        buttonText: slider.buttonText || '',
        buttonLink: slider.buttonLink || '',
        image: slider.image || '',
        textPosition: slider.textPosition || 'left',
        order: slider.order || 0,
        isActive: slider.isActive !== false,
      });
    } else {
      setEditingSlider(null);
      setFormData({
        title: '',
        subtitle: '',
        description: '',
        buttonText: '',
        buttonLink: '',
        image: '',
        textPosition: 'left',
        order: sliders.length,
        isActive: true,
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingSlider(null);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);
      
      // Assuming there's an upload endpoint - adjust as needed
      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formDataUpload,
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: data.url }));
        toast.success('Image uploaded');
      } else {
        // Fallback: use file preview
        const reader = new FileReader();
        reader.onloadend = () => {
          setFormData(prev => ({ ...prev, image: reader.result }));
        };
        reader.readAsDataURL(file);
        toast.success('Image ready');
      }
    } catch (error) {
      // Fallback: use file preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, image: reader.result }));
      };
      reader.readAsDataURL(e.target.files[0]);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.image) {
      toast.error('Please provide a title and image');
      return;
    }

    try {
      if (editingSlider) {
        await slidersAPI.update(editingSlider._id, formData);
        toast.success('Slider updated');
      } else {
        await slidersAPI.create(formData);
        toast.success('Slider created');
      }
      closeModal();
      fetchSliders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to save slider');
    }
  };

  const handleDelete = async () => {
    try {
      await slidersAPI.delete(deletingId);
      toast.success('Slider deleted');
      setDeletingId(null);
      fetchSliders();
    } catch (error) {
      toast.error('Failed to delete slider');
    }
  };

  const toggleActive = async (id, isActive) => {
    try {
      await slidersAPI.update(id, { isActive: !isActive });
      toast.success(`Slider ${isActive ? 'hidden' : 'visible'}`);
      fetchSliders();
    } catch (error) {
      toast.error('Failed to update slider');
    }
  };

  const textPositions = [
    { value: 'left', label: 'Left' },
    { value: 'center', label: 'Center' },
    { value: 'right', label: 'Right' },
  ];

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Hero Sliders</h1>
          <p className="text-gray-500">Manage homepage hero slider images</p>
        </div>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-luxury-charcoal text-white rounded-lg hover:bg-gray-800"
        >
          <HiOutlinePlus className="w-5 h-5" />
          Add Slider
        </button>
      </div>

      {/* Sliders Grid */}
      <div className="bg-white rounded-lg shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="spinner" />
          </div>
        ) : sliders.length === 0 ? (
          <div className="text-center py-20">
            <HiOutlinePhotograph className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No sliders yet</p>
            <button 
              onClick={() => openModal()}
              className="mt-4 text-luxury-gold hover:underline"
            >
              Create your first slider
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {sliders.sort((a, b) => a.order - b.order).map((slider, index) => (
              <div 
                key={slider._id}
                className={`flex items-start gap-4 p-4 border rounded-lg ${
                  slider.isActive ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'
                }`}
              >
                {/* Drag Handle */}
                <div className="flex items-center justify-center w-8 h-8 text-gray-400 cursor-move">
                  <HiMenuAlt4 className="w-5 h-5" />
                </div>

                {/* Image Preview */}
                <div className="w-48 h-28 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                  {slider.image ? (
                    <img 
                      src={slider.image} 
                      alt={slider.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <HiOutlinePhotograph className="w-8 h-8 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="font-semibold text-lg">{slider.title}</h3>
                      {slider.subtitle && (
                        <p className="text-gray-500 text-sm">{slider.subtitle}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        slider.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {slider.isActive ? 'Active' : 'Hidden'}
                      </span>
                    </div>
                  </div>

                  {slider.description && (
                    <p className="text-gray-600 text-sm mt-2 line-clamp-2">{slider.description}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-gray-500">
                    <span>Position: {slider.textPosition || 'Left'}</span>
                    {slider.buttonText && (
                      <span>Button: "{slider.buttonText}"</span>
                    )}
                    <span>Order: {index + 1}</span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleActive(slider._id, slider.isActive)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                    title={slider.isActive ? 'Hide' : 'Show'}
                  >
                    {slider.isActive ? (
                      <HiOutlineEyeOff className="w-5 h-5" />
                    ) : (
                      <HiOutlineEye className="w-5 h-5" />
                    )}
                  </button>
                  <button
                    onClick={() => openModal(slider)}
                    className="p-2 text-gray-400 hover:text-gray-600"
                  >
                    <HiOutlinePencil className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setDeletingId(slider._id)}
                    className="p-2 text-gray-400 hover:text-red-600"
                  >
                    <HiOutlineTrash className="w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Slider Form Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {editingSlider ? 'Edit Slider' : 'Create Slider'}
              </h3>
              <button onClick={closeModal}>
                <HiOutlineX className="w-6 h-6" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Slider Image *
                </label>
                <div className="border-2 border-dashed border-gray-200 rounded-lg p-4">
                  {formData.image ? (
                    <div className="relative aspect-[21/9] rounded-lg overflow-hidden bg-gray-100">
                      <img 
                        src={formData.image} 
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, image: '' }))}
                        className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-lg"
                      >
                        <HiOutlineX className="w-5 h-5" />
                      </button>
                    </div>
                  ) : (
                    <label className="flex flex-col items-center justify-center py-8 cursor-pointer">
                      <HiOutlinePhotograph className="w-12 h-12 text-gray-400 mb-2" />
                      <span className="text-gray-500">
                        {uploading ? 'Uploading...' : 'Click to upload image'}
                      </span>
                      <span className="text-gray-400 text-sm mt-1">
                        Recommended: 1920x800 pixels
                      </span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Title & Subtitle */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    placeholder="New Collection"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle
                  </label>
                  <input
                    type="text"
                    value={formData.subtitle}
                    onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    placeholder="Spring/Summer 2024"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold resize-none"
                  placeholder="Discover our latest collection of luxury items..."
                />
              </div>

              {/* Button Text & Link */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Text
                  </label>
                  <input
                    type="text"
                    value={formData.buttonText}
                    onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    placeholder="Shop Now"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Link
                  </label>
                  <input
                    type="text"
                    value={formData.buttonLink}
                    onChange={(e) => setFormData({ ...formData, buttonLink: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    placeholder="/shop/new-arrivals"
                  />
                </div>
              </div>

              {/* Text Position & Order */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Text Position
                  </label>
                  <div className="flex gap-2">
                    {textPositions.map((pos) => (
                      <button
                        key={pos.value}
                        type="button"
                        onClick={() => setFormData({ ...formData, textPosition: pos.value })}
                        className={`flex-1 px-4 py-2 border rounded-lg text-sm ${
                          formData.textPosition === pos.value
                            ? 'border-luxury-charcoal bg-luxury-charcoal text-white'
                            : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        {pos.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Order
                  </label>
                  <input
                    type="number"
                    value={formData.order}
                    onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-luxury-gold"
                    min="0"
                  />
                </div>
              </div>

              {/* Active */}
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-luxury-gold"
                />
                <label htmlFor="isActive" className="text-sm text-gray-700">
                  Show on homepage
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-luxury-charcoal text-white rounded-lg hover:bg-gray-800"
                >
                  {editingSlider ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deletingId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold mb-2">Delete Slider</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this slider? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeletingId(null)}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSliders;
